/**
 * CellTaskBadge — live task-status pill, reused in:
 *   - PostsGrid MasterCell / DerivativeCell (post regen + generation)
 *   - ResearchRunCard per-phase chips
 *   - TopicDetailPanel ResearchTab StepPanel buttons
 *
 * Reads from the global `background-tasks` Zustand store via `useTask(taskId)`.
 * No own Firestore listener — store is the singleton listener mounted in the
 * menu sidebar via `useTaskStoreConnection`.
 *
 * States:
 *   - taskId null OR task null OR status terminal-non-error → renders null
 *     unless fallbackActive is set by a caller with an independent running flag.
 *   - status pending|running|retrying|cancelling → primary chip with current
 *     phase displayName + small spinner.
 *   - status error → red chip with first 80 chars of `task.error`.
 */
import React, { useState } from 'react';
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { useTask, ACTIVE_STATUSES } from '@modules/background-tasks';
import type { BackendTask, TaskPhaseDefinition } from '@modules/background-tasks';

export interface CellTaskBadgeProps {
  taskId: string | null | undefined;
  /** Override label (when caller wants something other than the phase displayName). */
  label?: string;
  /** Render an active-looking pill even before the backend task is present in the task store. */
  fallbackActive?: boolean;
  /** Compact pill (no spinner, smaller padding). Default false. */
  dense?: boolean;
  'data-testid'?: string;
}

function pickActivePhase(task: BackendTask): TaskPhaseDefinition | null {
  const phases = task.phases ?? null;
  if (!phases) return null;
  const currentKey = (task as { currentPhaseKey?: string }).currentPhaseKey;
  if (currentKey && phases[currentKey]) return phases[currentKey];
  // Fallback — pick the first 'running' phase, then first 'pending'.
  const ordered = Object.values(phases).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  return (
    ordered.find((p) => p.status === 'running') ??
    ordered.find((p) => p.status === 'pending') ??
    null
  );
}

function renderActivePill({
  label,
  dense,
  testId,
}: {
  label: string;
  dense: boolean;
  testId: string | undefined;
}) {
  return (
    <Tooltip title={`${label} · running`}>
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        icon={<CircularProgress size={dense ? 10 : 12} thickness={5} />}
        label={label}
        data-testid={testId ?? 'cell-task-badge-fallback'}
        sx={{
          height: dense ? 18 : 22,
          fontSize: dense ? '0.65rem' : '0.7rem',
          fontFamily: 'monospace',
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          maxWidth: 220,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </Tooltip>
  );
}

export const CellTaskBadge: React.FC<CellTaskBadgeProps> = ({
  taskId,
  label,
  fallbackActive = false,
  dense = false,
  'data-testid': testId,
}) => {
  const { task } = useTask(taskId ?? null);
  // LR2 — persistent full-error dialog (declared before any early return).
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  if (!taskId || !task) {
    if (!fallbackActive) return null;
    return renderActivePill({ label: label ?? 'Running', dense, testId });
  }

  const status = task.status as BackendTask['status'];
  const isActive = ACTIVE_STATUSES.includes(status as typeof ACTIVE_STATUSES[number]);
  const isError = status === 'error';
  if (!isActive && !isError) {
    if (!fallbackActive) return null;
    return renderActivePill({ label: label ?? 'Running', dense, testId });
  }

  if (isError) {
    const fullError = String(task.error ?? 'Failed');
    const msg = fullError.slice(0, 80);
    // LR2 rule: the truncated chip is NOT the only error surface — clicking
    // it opens a dialog with the FULL, selectable error text.
    return (
      <>
        <Tooltip title={`${fullError.slice(0, 300)} — click for full error`}>
          <Chip
            size="small"
            color="error"
            label={msg}
            onClick={(event) => {
              event.stopPropagation();
              setErrorDialogOpen(true);
            }}
            data-testid={testId ?? 'cell-task-badge-error'}
            sx={{
              height: dense ? 18 : 22,
              fontSize: dense ? '0.65rem' : '0.7rem',
              fontFamily: 'monospace',
              maxWidth: 240,
              cursor: 'pointer',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </Tooltip>
        <SDialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          title="Task error"
          maxWidth="sm"
          fullWidth
          data-testid="cell-task-badge-error-dialog"
        >
          <Box
            sx={{
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              userSelect: 'text',
            }}
          >
            {fullError}
          </Box>
        </SDialog>
      </>
    );
  }

  const phase = pickActivePhase(task);
  const phaseLabel = label
    ?? phase?.displayName
    ?? phase?.name
    ?? task.action
    ?? 'Generating';
  const tooltip = phase
    ? `${phaseLabel}${phase.status === 'running' ? ' · running' : ''}`
    : phaseLabel;

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        icon={<CircularProgress size={dense ? 10 : 12} thickness={5} />}
        label={phaseLabel}
        data-testid={testId ?? 'cell-task-badge'}
        sx={{
          height: dense ? 18 : 22,
          fontSize: dense ? '0.65rem' : '0.7rem',
          fontFamily: 'monospace',
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          maxWidth: 220,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </Tooltip>
  );
};

export default CellTaskBadge;
